import { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { DISCIPLINES, type DisciplineSlug } from "@jumpbean/shared";
import { CharacterImage, Logo } from "../components";

/**
 * PublicLayout - Layout for unauthenticated pages (landing, login, register, pricing).
 *
 * Features:
 * - Sticky header with smooth scroll background change
 * - Navigation with smooth scroll to sections
 * - Full footer with links and character parade
 */
export function PublicLayout() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const isHomepage = location.pathname === "/";

  return (
    <div className="min-h-screen bg-gradient-to-b from-sunshine-100 via-cloud to-sky-100 flex flex-col">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-magic-600 focus:text-white focus:rounded-lg"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header
        className={`
          fixed top-0 left-0 right-0 z-50
          transition-all duration-300
          ${isScrolled ? "bg-white/95 backdrop-blur-md shadow-playful" : "bg-transparent"}
        `}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center hover:opacity-90 transition-opacity -my-2"
            >
              <Logo variant="full" size="lg" alt="JumpBean - Home" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {isHomepage && (
                <>
                  <NavLink href="#characters">Characters</NavLink>
                  <NavLink href="#how-it-works">How It Works</NavLink>
                  <NavLink href="#pricing">Pricing</NavLink>
                </>
              )}
              {!isHomepage && (
                <>
                  <NavLink href="/">Home</NavLink>
                  <NavLink href="/pricing">Pricing</NavLink>
                </>
              )}
              <NavLink href="/about">About</NavLink>
              <NavLink href="/blog">Blog</NavLink>
            </nav>

            {/* Auth buttons */}
            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/login"
                className="font-body font-semibold text-neutral-700 hover:text-magic-600 transition-colors px-4 py-2 rounded-lg hover:bg-magic-50"
              >
                Log In
              </Link>
              <Link
                to="/register"
                className="font-display font-bold text-neutral-900 bg-sunshine-400 hover:bg-sunshine-500 px-6 py-3 rounded-full transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
              >
                Start Free
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-12 h-12 flex items-center justify-center rounded-lg hover:bg-neutral-100 transition-colors"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              <div className="relative w-6 h-5">
                <span
                  className={`absolute left-0 h-0.5 w-6 bg-neutral-900 transition-all duration-300 ${
                    mobileMenuOpen ? "top-2 rotate-45" : "top-0"
                  }`}
                />
                <span
                  className={`absolute left-0 top-2 h-0.5 w-6 bg-neutral-900 transition-opacity duration-300 ${
                    mobileMenuOpen ? "opacity-0" : "opacity-100"
                  }`}
                />
                <span
                  className={`absolute left-0 h-0.5 w-6 bg-neutral-900 transition-all duration-300 ${
                    mobileMenuOpen ? "top-2 -rotate-45" : "top-4"
                  }`}
                />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`
            md:hidden bg-white border-t border-neutral-100
            overflow-hidden transition-all duration-300
            ${mobileMenuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}
          `}
        >
          <nav className="px-4 py-4 space-y-2">
            {isHomepage && (
              <>
                <MobileNavLink href="#characters" onClick={() => setMobileMenuOpen(false)}>
                  Characters
                </MobileNavLink>
                <MobileNavLink href="#how-it-works" onClick={() => setMobileMenuOpen(false)}>
                  How It Works
                </MobileNavLink>
                <MobileNavLink href="#pricing" onClick={() => setMobileMenuOpen(false)}>
                  Pricing
                </MobileNavLink>
              </>
            )}
            {!isHomepage && (
              <>
                <MobileNavLink href="/" onClick={() => setMobileMenuOpen(false)}>
                  Home
                </MobileNavLink>
                <MobileNavLink href="/pricing" onClick={() => setMobileMenuOpen(false)}>
                  Pricing
                </MobileNavLink>
              </>
            )}
            <MobileNavLink href="/about" onClick={() => setMobileMenuOpen(false)}>
              About
            </MobileNavLink>
            <MobileNavLink href="/blog" onClick={() => setMobileMenuOpen(false)}>
              Blog
            </MobileNavLink>

            <div className="pt-4 border-t border-neutral-100 space-y-2">
              <Link
                to="/login"
                className="block w-full text-center font-body font-semibold text-neutral-700 hover:text-magic-600 transition-colors px-4 py-3 rounded-lg hover:bg-magic-50"
              >
                Log In
              </Link>
              <Link
                to="/register"
                className="block w-full text-center font-display font-bold text-neutral-900 bg-sunshine-400 hover:bg-sunshine-500 px-6 py-3 rounded-full transition-all"
              >
                Start Free
              </Link>
            </div>
          </nav>
        </div>
      </header>

      {/* Main content with top padding for fixed header */}
      <main id="main-content" className="flex-1 flex flex-col pt-16 md:pt-20">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-neutral-900 text-white">
        {/* Character parade */}
        <div className="border-b border-neutral-800">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex justify-center gap-6 md:gap-12">
              {(Object.keys(DISCIPLINES) as DisciplineSlug[]).map((discipline, index) => (
                <div
                  key={discipline}
                  className="animate-float opacity-80 hover:opacity-100 transition-opacity"
                  style={{ animationDelay: `${index * 0.3}s` }}
                >
                  <CharacterImage discipline={discipline} expression="happy" size="md" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer content */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {/* Brand column */}
            <div className="md:col-span-2">
              <Link to="/" className="inline-flex items-center gap-1.5 mb-4">
                <Logo variant="bean" size="xl" alt="JumpBean" />
                <span className="font-display font-bold text-2xl text-white">JumpBean</span>
              </Link>
              <p className="font-body text-neutral-400 max-w-sm">
                Magical learning adventures for preschoolers. Math, spelling, patterns, typing, and
                coding—all through play.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-display font-bold text-body-lg mb-4">Product</h4>
              <ul className="space-y-2">
                <FooterLink href="/#characters">Characters</FooterLink>
                <FooterLink href="/#how-it-works">How It Works</FooterLink>
                <FooterLink href="/pricing">Pricing</FooterLink>
                <FooterLink href="/about">About</FooterLink>
                <FooterLink href="/blog">Blog</FooterLink>
              </ul>
            </div>

            <div>
              <h4 className="font-display font-bold text-body-lg mb-4">Legal</h4>
              <ul className="space-y-2">
                <FooterLink href="/privacy">Privacy Policy</FooterLink>
                <FooterLink href="/terms">Terms of Service</FooterLink>
                <FooterLink href="/coppa">COPPA Compliance</FooterLink>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 border-t border-neutral-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="font-body text-body-sm text-neutral-500">
              &copy; {new Date().getFullYear()} JumpBean. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <span className="font-body text-body-sm text-neutral-500">
                Made with 💜 for little learners
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

/** Desktop navigation link */
function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
  // Handle both anchor links and route links
  const isAnchor = href.startsWith("#");

  if (isAnchor) {
    return (
      <a
        href={href}
        className="font-body font-semibold text-neutral-700 hover:text-magic-600 transition-colors px-4 py-2 rounded-lg hover:bg-magic-50"
        onClick={(e) => {
          e.preventDefault();
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      to={href}
      className="font-body font-semibold text-neutral-700 hover:text-magic-600 transition-colors px-4 py-2 rounded-lg hover:bg-magic-50"
    >
      {children}
    </Link>
  );
}

/** Mobile navigation link */
function MobileNavLink({
  href,
  onClick,
  children,
}: {
  href: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const isAnchor = href.startsWith("#");

  if (isAnchor) {
    return (
      <a
        href={href}
        className="block font-body font-semibold text-neutral-700 hover:text-magic-600 transition-colors px-4 py-3 rounded-lg hover:bg-magic-50"
        onClick={(e) => {
          e.preventDefault();
          onClick();
          setTimeout(() => {
            const element = document.querySelector(href);
            if (element) {
              element.scrollIntoView({ behavior: "smooth" });
            }
          }, 100);
        }}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      to={href}
      className="block font-body font-semibold text-neutral-700 hover:text-magic-600 transition-colors px-4 py-3 rounded-lg hover:bg-magic-50"
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

/** Footer link */
function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
  const location = useLocation();
  const isExternal = href.startsWith("http");
  const isAnchor = href.startsWith("/#");

  if (isAnchor) {
    const hash = href.slice(1); // Remove leading "/" to get "#section"
    const isOnHomepage = location.pathname === "/";

    return (
      <li>
        <a
          href={href}
          className="font-body text-body-sm text-neutral-400 hover:text-white transition-colors"
          onClick={(e) => {
            if (isOnHomepage) {
              e.preventDefault();
              const element = document.querySelector(hash);
              if (element) {
                element.scrollIntoView({ behavior: "smooth" });
              }
            }
            // If not on homepage, let the default navigation happen
          }}
        >
          {children}
        </a>
      </li>
    );
  }

  if (isExternal) {
    return (
      <li>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="font-body text-body-sm text-neutral-400 hover:text-white transition-colors"
        >
          {children}
        </a>
      </li>
    );
  }

  return (
    <li>
      <Link
        to={href}
        className="font-body text-body-sm text-neutral-400 hover:text-white transition-colors"
      >
        {children}
      </Link>
    </li>
  );
}

export default PublicLayout;
